import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Sparkles, X, Send, Loader2, Phone, FileText, ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useOccasion } from "@/occasion/OccasionProvider";
import { BRAND } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "sav_smsma_session";

const renderInline = (text) =>
  (text || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    /^\*\*[^*]+\*\*$/.test(p) ? <strong key={i}>{p.slice(2, -2)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>);

const MarkdownLite = ({ text }) => {
  const lines = (text || "").split(/\r?\n/);
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) {
      blocks.push(<div key={blocks.length} className="font-semibold text-navy mt-2.5 mb-1">{renderInline(h[2])}</div>);
      i++; continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*]\s+/, "")); i++; }
      blocks.push(<ul key={blocks.length} className="list-disc ms-5 my-1.5 space-y-1">{items.map((it, k) => <li key={k}>{renderInline(it)}</li>)}</ul>);
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s+/, "")); i++; }
      blocks.push(<ol key={blocks.length} className="list-decimal ms-5 my-1.5 space-y-1">{items.map((it, k) => <li key={k}>{renderInline(it)}</li>)}</ol>);
      continue;
    }
    const para = [line]; i++;
    while (i < lines.length && lines[i].trim() && !/^\s*([-*]|\d+\.)\s+/.test(lines[i]) && !/^#{1,3}\s+/.test(lines[i])) { para.push(lines[i]); i++; }
    blocks.push(<p key={blocks.length} className="my-1.5 leading-relaxed">{para.map((pl, k) => <React.Fragment key={k}>{renderInline(pl)}{k < para.length - 1 ? <br /> : null}</React.Fragment>)}</p>);
  }
  return <div className="space-y-0.5">{blocks}</div>;
};

function sessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) { s = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())); localStorage.setItem(SESSION_KEY, s); }
  return s;
}

export const SmsmaWidget = () => {
  const { t, lang, isAr } = useLang();
  const { occasion } = useOccasion();
  const [open, setOpen] = useState(false);
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]);
  const [handoff, setHandoff] = useState(null);
  const threadRef = useRef(null);
  const atBottomRef = useRef(true);
  const [showJump, setShowJump] = useState(false);

  const scrollToBottom = () => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  const onScroll = () => {
    const el = threadRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    atBottomRef.current = atBottom;
    setShowJump(!atBottom);
  };
  useEffect(() => { if (atBottomRef.current) scrollToBottom(); }, [thread, busy, open]);

  const start = async () => {
    setOpen(true);
    if (conv) return;
    setBusy(true);
    try {
      const path = window.location.pathname;
      const r = await axios.post(`${API}/smsma/conversation`, { session_id: sessionId(), source_page: path, lang });
      setConv(r.data.conversation);
      const base = r.data.greeting?.[lang] || r.data.greeting?.en || "";
      // Deterministic occasion greeting (registry text only — ZERO extra AI calls).
      // Only present when the engine is ON and an occasion is active.
      const occGreet = occasion ? (lang === "ar" ? occasion.greeting_ar : occasion.greeting_en) : "";
      const first = occGreet ? `${occGreet}\n\n${base}` : base;
      setThread([{ role: "assistant", text: first }]);
    } catch (e) { /* keep widget open even if backend hiccups */ }
    finally { setBusy(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !conv || busy) return;
    const q = text.trim();
    setThread((p) => [...p, { role: "user", text: q }]);
    setText(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/smsma/message`, { conversation_id: conv.id, text: q, topic: q.slice(0, 40), lang });
      const reply = r.data.ai_enabled ? r.data.reply : (r.data.message?.[lang] || r.data.message?.en || "");
      if (reply) setThread((p) => [...p, { role: "assistant", text: reply }]);
      if (r.data.handoff) setHandoff({ summary: r.data.summary, wa: r.data.whatsapp_url });
    } catch (e) {
      setThread((p) => [...p, { role: "assistant", text: t({
        en: "I received your full message and it's been preserved, but I couldn't complete the analysis right now. You can retry, or continue with the Smart Account Vision team without re-entering your case.",
        ar: "لقد استلمت رسالتك كاملةً وتم الاحتفاظ بها، لكن تعذّر إكمال التحليل الآن. يمكنك إعادة المحاولة أو المتابعة مع فريق سمارت أكاونت فيجن دون إعادة إدخال حالتك.",
      }) }]);
      setHandoff({ summary: q, wa: `https://wa.me/${BRAND.phoneRaw}?text=${encodeURIComponent(q)}` });
    } finally { setBusy(false); }
  };

  const track = (type, detail) => {
    if (conv) axios.post(`${API}/smsma/event`, { conversation_id: conv.id, type, detail }).catch(() => {});
  };

  return (
    <>
      {!open && (
        <button onClick={start} data-testid="smsma-launcher" aria-label="Ask Smsma"
          className="fixed bottom-24 end-6 z-[70] inline-flex items-center gap-2 h-13 px-5 rounded-full bg-navy text-white shadow-[0_12px_30px_-8px_rgba(15,32,64,0.7)] hover:bg-navy-600 hover:scale-[1.03] transition-all duration-300">
          <Sparkles className="h-5 w-5 text-gold" />
          <span className="font-semibold text-sm">{t({ en: "Ask Smsma", ar: "اسأل سمسمة" })}</span>
        </button>
      )}

      {open && (
        <div dir={isAr ? "rtl" : "ltr"} data-testid="smsma-panel"
          className="fixed bottom-6 start-4 end-4 sm:start-auto sm:end-6 z-[80] sm:w-[380px] max-h-[75vh] flex flex-col rounded-2xl border border-line bg-white shadow-lift overflow-hidden">
          <div className="flex items-center justify-between bg-navy text-white px-4 py-3">
            <div className="flex items-center gap-2 font-display"><Sparkles className="h-5 w-5 text-gold" /> {t({ en: "Smsma · SAV Assistant", ar: "سمسمة · مساعد SAV" })}</div>
            <button onClick={() => setOpen(false)} data-testid="smsma-close" aria-label="Close" className="p-1 rounded-full hover:bg-white/10"><X className="h-4 w-4" /></button>
          </div>

          <div ref={threadRef} onScroll={onScroll} className="flex-1 overflow-y-auto p-4 space-y-3 bg-surface min-h-[260px]" data-testid="smsma-thread">
            {thread.map((m, i) => (
              <div key={i} className={`rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "ms-auto max-w-[85%] bg-navy text-white leading-snug" : "max-w-[94%] bg-white border border-line text-ink"}`}>
                {m.role === "user" ? m.text : <MarkdownLite text={m.text} />}
              </div>
            ))}
            {busy && <div className="flex items-center gap-2 bg-white border border-line rounded-2xl px-3.5 py-2.5 text-xs text-muted"><Loader2 className="h-4 w-4 animate-spin text-navy" /> {t({ en: "Analysing your case… this can take up to a minute", ar: "يتم تحليل حالتك… قد يستغرق ذلك حتى دقيقة" })}</div>}

            {handoff && (
              <div className="rounded-2xl border border-gold/40 bg-gold/[0.06] p-3.5 space-y-2" data-testid="smsma-handoff">
                <a href={handoff.wa || `https://wa.me/${BRAND.phoneRaw}`} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp", "")}
                  data-testid="smsma-whatsapp" className="flex items-center justify-center gap-2 h-11 rounded-full bg-brandgreen text-white font-semibold text-sm hover:bg-brandgreen-600 transition-colors">
                  {t({ en: "Continue on WhatsApp", ar: "أكمل عبر واتساب" })}
                </a>
                <div className="grid grid-cols-2 gap-2">
                  <a href={`tel:${BRAND.phoneRaw}`} onClick={() => track("call", "")} data-testid="smsma-call"
                    className="flex items-center justify-center gap-1.5 h-11 rounded-full bg-navy text-white font-semibold text-sm hover:bg-navy-600 transition-colors"><Phone className="h-4 w-4" /> {t({ en: "Call us", ar: "اتصل بنا" })}</a>
                  <a href="/#contact" onClick={() => track("quote", "")} data-testid="smsma-quote"
                    className="flex items-center justify-center gap-1.5 h-11 rounded-full bg-white border border-line text-navy font-semibold text-sm hover:border-navy transition-colors"><FileText className="h-4 w-4" /> {t({ en: "Get a quote", ar: "اطلب عرضاً" })}</a>
                </div>
              </div>
            )}
          </div>

          {showJump && (
            <button onClick={() => { scrollToBottom(); setShowJump(false); atBottomRef.current = true; }}
              data-testid="smsma-jump-latest"
              className="absolute bottom-[68px] left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-navy text-white text-xs font-semibold shadow-lift hover:bg-navy-600 transition-colors">
              <ArrowDown className="h-3.5 w-3.5" /> {t({ en: "Latest", ar: "الأحدث" })}
            </button>
          )}
          <form onSubmit={send} className="p-3 border-t border-line flex items-center gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} data-testid="smsma-input"
              disabled={!conv}
              placeholder={t({ en: "Type your question…", ar: "اكتب سؤالك…" })}
              className="flex-1 h-11 px-4 rounded-full border border-line focus:outline-none focus:border-navy text-sm disabled:bg-surface disabled:opacity-70" />
            <button type="submit" disabled={busy || !conv} data-testid="smsma-send" aria-label="Send"
              className="h-11 w-11 grid place-items-center rounded-full bg-navy text-white hover:bg-navy-600 transition-colors disabled:opacity-60 shrink-0">
              <Send className={`h-4 w-4 ${isAr ? "rotate-180" : ""}`} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
